import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';
import { deleteDeletedImage, listDeletedImages, type DeletedImage } from '../service/deleted-images.service';

  const getFilenameFromUrl = (url?: string | null, fallback: string = 'download') => {
    if (!url) return fallback;
    try {
      const u = new URL(url);
      const name = u.pathname.split('/').filter(Boolean).pop() || fallback;
      return name;
    } catch {
      return fallback;
    }
  };

  const downloadUrl = async (url?: string | null, filename?: string) => {
    if (!url) return;
    const name = filename || getFilenameFromUrl(url);
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    } catch {
      // Fallback: direct link click
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };


const PAGE_SIZE = 100;

function SecondPage() {
  const [items, setItems] = React.useState<DeletedImage[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [query, setQuery] = React.useState('');

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmId, setConfirmId] = React.useState<string | null>(null);
  const [confirmBusy, setConfirmBusy] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await listDeletedImages({ page, pageSize: PAGE_SIZE, search: query || undefined });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyToClipboard = async (text?: string | null) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Deleted Images</Typography>
        <TextField
          size="small"
          placeholder="Search in title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        <Button variant="contained" onClick={() => { setPage(1); setQuery(search.trim()); }}>Search</Button>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2">Page {page} / {totalPages}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" disabled={loading || page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
        <Button size="small" disabled={loading || page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
      </Stack>

      <Box sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          sm: '1fr 1fr',
          md: '1fr 1fr 1fr',
          lg: '1fr 1fr 1fr 1fr'
        }
      }}>
        {items.map((it) => {
          const thumb = it.thumbnailUrl || it.imageUrl;
          return (
            <Box key={it.id} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ImageIcon fontSize="small" style={{ marginRight: 6 }} />
                <Typography variant="subtitle2" noWrap title={it.title} sx={{ flexGrow: 1 }}>{it.title}</Typography>
                <Tooltip title="Copy thumbnail URL">
                  <IconButton size="small" onClick={() => copyToClipboard(it.thumbnailUrl)}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download thumbnail">
                  <IconButton size="small" onClick={() => downloadUrl(it.thumbnailUrl)}>
                    <DownloadIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy image URL">
                  <IconButton size="small" onClick={() => copyToClipboard(it.imageUrl)}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download image">
                  <IconButton size="small" onClick={() => downloadUrl(it.imageUrl)}>
                    <DownloadIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy upscaled URL">
                  <IconButton size="small" onClick={() => copyToClipboard(it.upscaledImageUrl)}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download upscaled">
                  <IconButton size="small" onClick={() => downloadUrl(it.upscaledImageUrl)}>
                    <DownloadIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => { setConfirmId(it.id); setConfirmOpen(true); }}>
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ cursor: 'pointer' }} onClick={() => { setPreviewUrl(it.imageUrl); setPreviewOpen(true); }}>
                <img src={thumb} alt={it.title} style={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 8 }} />
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Full image preview */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullScreen>
        <DialogTitle>Image Preview</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0 }}>
          {previewUrl && (
            <img src={previewUrl} alt="full" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={confirmOpen} onClose={() => !confirmBusy && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent dividers>
          <Typography>Bu resmi AWS ve tablodan silmek istediğinize emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={confirmBusy}>Vazgeç</Button>
          <Button
            variant="contained"
            color="error"
            disabled={confirmBusy || !confirmId}
            onClick={async () => {
              if (!confirmId) return;
              setConfirmBusy(true);
              try {
                await deleteDeletedImage(confirmId);
                setConfirmOpen(false);
                setConfirmId(null);
                fetchData();
              } finally {
                setConfirmBusy(false);
              }
            }}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SecondPage;
